import { useEffect, type ComponentProps } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { shouldClearIdentityCache } from "@bastion/data";
import { useAuthStore } from "@/shared/stores/authStore";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
    },
  },
});

type QueryProviderProps = Pick<ComponentProps<typeof QueryClientProvider>, "children">;

export function QueryProvider({ children }: QueryProviderProps) {
  // The client is a module singleton, so its cache outlives a sign-out. Drop
  // it when the signed-in user changes, or the next user in this tab reads the
  // previous one's rows: most hook keys carry no user id, so nothing else
  // separates them. `shouldClearIdentityCache` states which transitions count
  // — a token refresh repeats the same id and must not clear.
  //
  // Subscribing to the store covers every path that sets a user (sign-out, the
  // `SIGNED_IN` and `SIGNED_OUT` events, a password sign-in, `initialize`), so
  // a new path added later needs no change here.
  useEffect(
    () =>
      useAuthStore.subscribe((state, previousState) => {
        if (shouldClearIdentityCache(previousState.user?.id, state.user?.id)) {
          queryClient.clear();
        }
      }),
    [],
  );

  return (
    <QueryClientProvider client={queryClient}>
      {children}
    </QueryClientProvider>
  );
}
